/**
 * /me — the caller's own profile, roles, and active grants.
 *
 * Visible-to-self PII (email, display_name) renders unconditionally
 * because the backend already decided this user can see their own
 * data. The UI never makes that authorisation decision itself
 * (CLAUDE.md §5.3).
 */

import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Container,
  Group,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useMsal } from "@azure/msal-react";
import { EditDisplayName } from "./EditDisplayName";
import { useMe } from "./useMe";

export function Me() {
  const { data, loading, error, errorCode, reload, update } = useMe();
  const { instance } = useMsal();

  if (loading) {
    return (
      <Container size="md" py="xl">
        <Text>Loading your profile…</Text>
      </Container>
    );
  }

  // Managed mode (ADR-0014): the token is valid but no administrator has added
  // this account. Show a clear, non-technical message + a way out, rather than
  // the generic error card.
  if (errorCode === "account_not_provisioned") {
    return (
      <Container size="sm" py="xl">
        <Alert variant="light" color="yellow" title="Your account isn't set up yet">
          <Stack gap="sm">
            <Text size="sm">
              You've signed in successfully, but an administrator hasn't added your account to this
              application. Please ask your administrator to grant you access, then sign in again.
            </Text>
            <Group>
              <Button variant="light" onClick={() => void instance.logoutPopup()}>
                Sign out
              </Button>
            </Group>
          </Stack>
        </Alert>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="md" py="xl">
        <Alert variant="light" color="red" title="Could not load your profile">
          <Stack gap="sm">
            <Text size="sm">{error}</Text>
            <Group>
              <Button onClick={reload} variant="light">
                Retry
              </Button>
            </Group>
          </Stack>
        </Alert>
      </Container>
    );
  }

  if (!data) return null;

  return (
    <Container size="md" py="xl">
      <Title order={2} mb="xl">
        {data.display_name}
      </Title>

      <Card withBorder radius="md" mb="md">
        <Title order={4} mb="sm">
          Identity
        </Title>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs" verticalSpacing="xs">
          <Field label="Display name">
            <EditDisplayName me={data} onUpdated={update} />
          </Field>
          <Field label="Email" value={data.email || "—"} />
          <Field label="Tenant" value={data.tenant_id} mono />
          <Field label="Status">
            <Badge color={statusColor(data.status)} variant="light">
              {data.status}
            </Badge>
          </Field>
          <Field label="Last login (UTC)" value={data.last_login_at_utc ?? "—"} />
          <Field label="Local user ID" value={data.user_id} mono />
          <Field label="Entra object ID" value={data.entra_object_id} mono />
        </SimpleGrid>
      </Card>

      <Card withBorder radius="md" mb="md">
        <Title order={4} mb="sm">
          Roles
        </Title>
        {data.roles.length === 0 ? (
          <Text c="dimmed" size="sm">
            (none)
          </Text>
        ) : (
          <Group gap="xs">
            {data.roles.map((r) => (
              <Badge key={r} color="brand" variant="light" size="lg">
                {r}
              </Badge>
            ))}
          </Group>
        )}
      </Card>

      <Card withBorder radius="md">
        <Title order={4} mb="sm">
          Grants
        </Title>
        {data.grants.length === 0 ? (
          <Text c="dimmed" size="sm">
            (none)
          </Text>
        ) : (
          <Stack gap="xs">
            {data.grants.map((g, i) => (
              <Group key={`${g.resource_type}-${i}`} gap="xs">
                <Badge color="teal" variant="light">
                  {g.resource_type}.{g.permission}
                </Badge>
                <Text size="sm" c="dimmed">
                  on
                </Text>
                <Anchor component="span" ff="monospace">
                  {g.resource_id ?? "(any)"}
                </Anchor>
              </Group>
            ))}
          </Stack>
        )}
      </Card>
    </Container>
  );
}

function statusColor(status: string): string {
  switch (status) {
    case "active":
      return "green";
    case "pending":
      return "yellow";
    case "deactivated":
      return "red";
    default:
      return "gray";
  }
}

interface FieldProps {
  label: string;
  value?: string;
  mono?: boolean;
  children?: React.ReactNode;
}

function Field({ label, value, mono, children }: FieldProps) {
  return (
    <div>
      <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
        {label}
      </Text>
      {children ? (
        <div style={{ marginTop: 2 }}>{children}</div>
      ) : (
        <Text size="sm" ff={mono ? "monospace" : undefined}>
          {value}
        </Text>
      )}
    </div>
  );
}
